import { useState } from 'react';
import {
  Anchor,
  Badge,
  Box,
  Checkbox,
  Collapse,
  Divider,
  Group,
  Paper,
  Popover,
  Stack,
  Text,
  Typography,
} from '@mantine/core';
import { BsPeopleCircle } from '@react-icons/all-files/bs/BsPeopleCircle';
import { AiOutlineDollar, AiTwotoneCalendar } from 'react-icons/ai';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import DOMPurify from 'dompurify';
import { isMobile } from 'react-device-detect';
import { useDisclosure } from '@mantine/hooks';
import { getAddressLink } from '@utils/JobUtils';
import { useJobContext } from '@company/state/jobContext';
import { JobUpdatableFields } from '@company/types/job.updatable.fields';
import * as api from '@company/services/job/api';
import IJobDetail from '@th-types/job.detail.type';
import JobPictures from '@components/JobPictures/JobPictures';
import JobSkills from './JobSkills';
import JobQuestions from './JobQuestions';
import JobActionsMenu from './JobActionsMenu';
import JobActionsBar from './JobActionsBar';
import ShareJobButton from './ShareJobButton';

const SERVER_URL = import.meta.env.VITE_SERVER_BASE_URL;

interface JobPanelProps {
  job: IJobDetail;
  onShareJob: () => void;
  onDeleteJob: () => void;
  panelHeight?: number;
}

export default function JobPanel({
  job,
  onShareJob,
  onDeleteJob,
  panelHeight,
}: JobPanelProps) {
  // Receive job by prop to display directly, but use states from context
  const {
    setActiveJobId,
    refetch,
    getJobYear,
    getLocalStartDate,
    getLocalEndDate,
  } = useJobContext();

  const [opened, { close, open }] = useDisclosure(false); // for internal members popover
  const [disabled, setDisabled] = useState(false);

  const [seeMore, { toggle: toggleSeeMore }] = useDisclosure(false); // mobile description

  // Action links, computed once and reused by the desktop row and the mobile menu
  const completeJobHref = job.datePublished
    ? `${SERVER_URL}/job/editJob/${job.id}`
    : `${SERVER_URL}/job/editEvent?eventId=${job.event.id}`;
  const editQuestionsHref = `${SERVER_URL}/job/enhance/${job.event.id}?jobId=${job.id}&isEditingQuestions=true`;

  const sortedJobs = [...job.event.eventJobIdsByAddress].sort();
  const numberOfJobs = job.event.eventJobIdsByAddress.length;

  const internalMembers =
    job.members?.filter((member) => member.isInternal) ?? [];
  const countInternalMembers =
    internalMembers.length > 1
      ? ` ${internalMembers.length} Internal members`
      : '1 Internal member';

  const nextJob = () => {
    if (sortedJobs) {
      const index = sortedJobs.indexOf(job.id);
      const nextId = sortedJobs[index + 1] || sortedJobs[0];
      setActiveJobId(nextId);
    }
  };

  const previousJob = () => {
    if (sortedJobs) {
      const index = sortedJobs.indexOf(job.id);
      const previousId =
        sortedJobs[index - 1] || sortedJobs[sortedJobs.length - 1];
      setActiveJobId(previousId);
    }
  };

  const getActiveJobIndex = () => {
    const index = sortedJobs?.indexOf(job.id);
    return index && index >= 0 ? index + 1 : 1;
  };

  const jobPositionElement = () => (
    <Group wrap="nowrap" flex={1} gap={2}>
      {numberOfJobs && numberOfJobs > 1 && (
        <FiChevronLeft
          className="cursor-point"
          size={22}
          onClick={previousJob}
        />
      )}
      <Text fw={700} truncate>
        {getActiveJobIndex()}/{numberOfJobs}
      </Text>
      {numberOfJobs && numberOfJobs > 1 && (
        <FiChevronRight className="cursor-point" size={22} onClick={nextJob} />
      )}
    </Group>
  );

  const handlePrivateJobChange = async (jobId: number) => {
    setDisabled(true);

    const { isJobPrivate } = job;

    try {
      const payload: JobUpdatableFields = {
        privateJob: !isJobPrivate,
      };

      await api.patchJob(jobId, payload);
    } catch (err) {
      console.log(err);
    } finally {
      refetch();
      setDisabled(false);
    }
  };

  const getStatusBadge = () => (
    <>
      {!job.datePublished && (
        <Badge variant="draft" tt="none">
          Draft
        </Badge>
      )}
      {!job.isActive && (
        <Badge variant="past" tt="none">
          Past
        </Badge>
      )}
    </>
  );

  const jobDescription = (
    <>
      <Typography>
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(job.description),
          }}
          style={{ whiteSpace: 'pre-wrap' }}
        />
      </Typography>
      <JobPictures pictures={job.pictures} />
      <JobSkills skills={job.skills} />
      <JobQuestions questions={job.questions} />
    </>
  );

  return (
    <Paper p={16} h={panelHeight} mb={16} style={{ overflowY: 'auto' }}>
      <Stack gap={4}>
        <Group>
          <Text fw={700} fz={18} lh={1} flex={1}>
            {job.eventName} {getStatusBadge()}
          </Text>
          <Group ml="auto" gap={0}>
            {isMobile ? (
              <>
                {jobPositionElement()}
                <JobActionsMenu
                  completeJobHref={completeJobHref}
                  editQuestionsHref={editQuestionsHref}
                  onDeleteJob={onDeleteJob}
                />
              </>
            ) : (
              jobPositionElement()
            )}
          </Group>
        </Group>
        <Text fz={14}>
          <Text span fw={700} inherit>
            {job.position}
          </Text>{' '}
          at{' '}
          <Text span inherit c="dimmed">
            {job.brandRepresented}
          </Text>
        </Text>
        <Text fz={14}>{getAddressLink(job.nationalJob, job.address)}</Text>

        <Group gap="xl">
          <Group gap={10}>
            <AiOutlineDollar size={20} />
            <Text fz={14}>
              ${job.payRate} per {job.payRatePeriod?.toString().toLowerCase()}
            </Text>
          </Group>
          {job.localizedStart && (
            <Group gap={10}>
              <AiTwotoneCalendar size={20} />
              <Text fz={14}>
                {getJobYear()}
                {getLocalStartDate()}
                &nbsp;-&nbsp;
                {job.localizedEnd && getLocalEndDate()}
                {job.ongoingJob && 'on-going'}
              </Text>
            </Group>
          )}
          <Group gap={10}>
            <BsPeopleCircle size={20} />
            <Text fz={14}>
              {job.totalPositions > 1
                ? `${job.totalPositions} people`
                : `${job.totalPositions} person`}
            </Text>
          </Group>
        </Group>
      </Stack>

      <Divider my={15} />

      <Group justify="space-between" mb={10}>
        <Checkbox
          label={
            <Text lh={1.2} fw={700}>
              Show this on Public Job Boards?
            </Text>
          }
          checked={!job.isJobPrivate}
          onChange={() => handlePrivateJobChange(job.id)}
          disabled={disabled}
        />
        <Popover
          position="bottom-start"
          withArrow
          arrowSize={10}
          shadow="md"
          opened={opened}
        >
          <Popover.Target>
            <Text
              td="underline"
              w="fit-content"
              onMouseEnter={open}
              onMouseLeave={close}
              fz={14}
              mt={-4}
            >
              {countInternalMembers}
            </Text>
          </Popover.Target>
          <Popover.Dropdown>
            {internalMembers.map((member) => (
              <Text key={member.id}>
                {member.firstName} {member.lastName}
              </Text>
            ))}
          </Popover.Dropdown>
        </Popover>
      </Group>
      {isMobile && (
        <Box mt={15}>
          <ShareJobButton onShareJob={onShareJob} />
        </Box>
      )}

      {/* ACTIONS (desktop only; mobile uses the header dropdown) */}
      {!isMobile && (
        <JobActionsBar
          completeJobHref={completeJobHref}
          editQuestionsHref={editQuestionsHref}
          onDeleteJob={onDeleteJob}
          onShareJob={onShareJob}
        />
      )}

      <Divider my={15} />

      {isMobile ? (
        <>
          <Group justify="flex-end">
            <Anchor c="black" fw={700} onClick={toggleSeeMore}>
              {seeMore ? 'See less' : 'See more'}
            </Anchor>
          </Group>
          <Collapse in={seeMore}>{jobDescription}</Collapse>
        </>
      ) : (
        jobDescription
      )}
    </Paper>
  );
}
